import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BrokerApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { formatPhone } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

export const BrokerSelect = ({ brokerId, onSelect, disabled = false }) => {
    const PER_PAGE = 20;
    let CACHE = {};
    const brokerApi = new BrokerApi.BrokerApi(applicationInsights);

    //Search
    const [brokerSearchLoading, set_brokerSearchLoading] = useState(false);
    const [brokers, set_brokers] = useState();
    const [brokerQuery, set_brokerQuery] = useState('');
    const [broker, set_broker] = useState();

    useEffect(() => {
        async function brokerIdChanged() {
            if (!brokerId) {
                return;
            }

            const response = await brokerApi.getLeanBroker(brokerId);
            if (response.statusCode !== 200) return;

            const bwr = response.result;
            set_broker(bwr);
            onSelect && onSelect(bwr);
        }

        brokerIdChanged();
    }, [brokerId]);

    const searchBrokers = async (query, page = 1) => {
        return await brokerApi.search(query, page, PER_PAGE);
    };

    const handleBrokerInputChange = (q) => {
        set_broker();
        q = (q ?? '').toLowerCase();
        set_brokerQuery(q);
    };

    const handleBrokerSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (CACHE[q]) {
            set_brokers(CACHE[q].options);
            return;
        }

        set_brokerSearchLoading(true);
        searchBrokers(q).then((resp) => {
            CACHE[q] = { options: resp.result, page: 1 };
            set_brokerSearchLoading(false);
            set_brokers(resp.result);
        });
    }, []);

    const handleBrokerPagination = (e, shownResults) => {
        const cachedQuery = CACHE[brokerQuery];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_brokerSearchLoading(true);
        const page = cachedQuery.page + 1;
        searchBrokers(brokerQuery, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            CACHE[brokerQuery] = { ...cachedQuery, options, page };
            set_brokerSearchLoading(false);
            set_brokers(options);
        });
    };

    const selectBroker = (broker) => {
        set_broker(broker);
        onSelect && onSelect(broker);
    };

    const clearBroker = () => {
        set_broker(null);
        onSelect && onSelect(null);
    };

    return (
        <>
            <label htmlFor='broker-search'>Broker</label>
            <div className='type-ahead-container'>
                <AsyncTypeahead
                    className='type-ahead'
                    id='broker-search'
                    isLoading={brokerSearchLoading}
                    labelKey={(option) => `${option.businessName ? option.businessName + ' - ' : ''}${option.fullName}`}
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleBrokerInputChange}
                    onPaginate={handleBrokerPagination}
                    onSearch={handleBrokerSearch}
                    onChange={(event) => selectBroker(event[0])}
                    options={brokers}
                    paginate
                    placeholder={disabled ? 'None Selected' : 'Search Brokers...'}
                    renderMenuItemChildren={(option) => (
                        <div key={option.id}>
                            <span>
                                {option.businessName ? option.businessName + ' - ' : ''}
                                {option.fullName}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={broker ? [broker] : []}
                    disabled={disabled || broker}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => clearBroker()}
                    disabled={!brokerId}
                    title='Remove selected broker from this loan'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            {broker && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        display: 'flex',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                    }}
                >
                    <div
                        style={{
                            minWidth: '250px',
                            float: 'left',
                            paddingRight: '20px',
                        }}
                    >
                        {broker.businessName && (
                            <div>
                                <b>{broker.businessName}</b>
                            </div>
                        )}
                        {broker.fullName && (
                            <div>
                                <b>{broker.fullName}</b>
                            </div>
                        )}
                        {broker.street1 && <div>{broker.street1}</div>}
                        {broker.street2 && <div>{broker.street2}</div>}
                        {broker.city && broker.state && broker.zipCode && (
                            <div>
                                {broker.city}, {broker.state} {broker.zipCode}
                            </div>
                        )}
                        {broker.email && <div>{broker.email}</div>}
                        {broker.phone && <div>{formatPhone(broker.phone)}</div>}
                    </div>
                </div>
            )}
        </>
    );
};
